import { useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import * as authApi from '../api/auth';
import { useAuth } from '../auth/AuthContext';
import { PasswordRulesHint } from '../components/PasswordRulesHint';
import { validatePassword } from '../validation/password';

/**
 * Set-new-password landing page — ROADMAP #18.1.
 *
 * Landed on from the magic link in the reset email
 * (`/reset-password?token=…`). The token's already in the URL; the
 * user picks a new password (twice for confirmation) and submits.
 * Server validates the token + password length, swaps the hash,
 * and returns a fresh access+refresh pair so the user is signed in
 * the moment they submit — they don't have to re-type credentials on
 * the next screen.
 *
 * No token in the URL? Show a friendly message pointing back at the
 * Forgot-password page rather than a blank form that will only fail
 * on submit.
 */
export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (token === '') {
    return (
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Stack spacing={3}>
          <Box
            component="img"
            src="/logo.svg"
            alt="10NEWTONS"
            sx={{ display: 'block', mx: 'auto', width: 'min(100%, 360px)' }}
          />
          <Typography variant="h4">Reset your password</Typography>
          <Alert severity="warning">
            This page needs a reset link to work. Request one from the{' '}
            <MuiLink component={Link} to="/forgot-password">
              Forgot password
            </MuiLink>{' '}
            page; the email we send contains the link to land you here.
          </Alert>
        </Stack>
      </Container>
    );
  }

  const policyError = validatePassword(password);
  const matches = password === confirm;
  const canSubmit = policyError === null && matches && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authApi.resetPassword(token, password);
      // The reset endpoint returns a TokenResponse and sets the
      // refresh cookie — prime AuthContext so the user is signed in
      // when they land on /dashboard.
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'password reset failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 8 }}>
      <Stack spacing={3}>
        <Box
          component="img"
          src="/logo.svg"
          alt="10NEWTONS"
          sx={{ display: 'block', mx: 'auto', width: 'min(100%, 360px)' }}
        />
        <Typography variant="h4">Set a new password</Typography>
        <Box component="form" onSubmit={handleSubmit} autoComplete="on">
          <Stack spacing={2}>
            <Box>
              <TextField
                label="New password"
                type="password"
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                disabled={submitting}
                error={password.length > 0 && policyError !== null}
              />
              <PasswordRulesHint value={password} />
            </Box>
            <TextField
              label="Confirm new password"
              type="password"
              fullWidth
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              disabled={submitting}
              helperText={
                confirm.length === 0
                  ? ' '
                  : matches
                    ? ' '
                    : 'passwords do not match'
              }
              error={confirm.length > 0 && !matches}
            />
            {error && <Alert severity="error">{error}</Alert>}
            <Button type="submit" variant="contained" disabled={!canSubmit}>
              {submitting ? 'Resetting…' : 'Set new password'}
            </Button>
          </Stack>
        </Box>
        <Typography variant="body2" color="text.secondary">
          <MuiLink component={Link} to="/login">
            Back to sign in
          </MuiLink>
        </Typography>
      </Stack>
    </Container>
  );
}
